import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { KPICard } from '@/components/charts/KPICard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useCouponPerformance, useReferralPerformance } from '@/api/bi';
import { useFilters, rangeISO } from '@/state/filters';
import { fmt } from '@/lib/format';
import { CouponsAnalyticsBody } from './CouponsPage';
import { ReferralsAnalyticsBody } from './ReferralsPage';

type PromotionsTab = 'coupons' | 'referrals';

export function CouponsReferralsPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: PromotionsTab = searchParams.get('tab') === 'referrals' ? 'referrals' : 'coupons';
  const iso = rangeISO(range, timeRangeMode);
  const filters = { start: iso.start, end: iso.end, storeIds };

  const coupons = useCouponPerformance({ ...filters, limit: 50 });
  const referrals = useReferralPerformance({ ...filters, limit: 50 });
  const couponTotals = coupons.data?.totals;
  const referralTotals = referrals.data?.totals;

  function handleTabChange(value: string) {
    const nextTab: PromotionsTab = value === 'referrals' ? 'referrals' : 'coupons';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', nextTab);
      return next;
    }, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Growth"
        title="Coupons & Referrals"
        description="Promotion performance across coupon redemptions, referral claims, incentive CAC, and customer value."
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          label="Coupon redemptions"
          value={fmt.number(couponTotals?.redemptions ?? 0)}
          loading={coupons.isLoading}
          error={coupons.isError}
          onRetry={() => void coupons.refetch()}
        />
        <KPICard
          label="Coupon revenue"
          value={fmt.npr(couponTotals?.gross_revenue ?? 0)}
          loading={coupons.isLoading}
          error={coupons.isError}
          onRetry={() => void coupons.refetch()}
        />
        <KPICard
          label="Discount cost"
          value={fmt.npr(couponTotals?.discount_amount ?? 0)}
          goodWhenUp={false}
          loading={coupons.isLoading}
          error={coupons.isError}
          onRetry={() => void coupons.refetch()}
        />
        <KPICard
          label="Coupon CAC"
          value={fmt.npr(couponTotals?.incentive_cac ?? null)}
          goodWhenUp={false}
          loading={coupons.isLoading}
          error={coupons.isError}
          onRetry={() => void coupons.refetch()}
        />
        <KPICard
          label="Referral claims"
          value={fmt.number(referralTotals?.claims ?? 0)}
          loading={referrals.isLoading}
          error={referrals.isError}
          onRetry={() => void referrals.refetch()}
        />
        <KPICard
          label="Referred users"
          value={fmt.number(referralTotals?.referred_users ?? 0)}
          loading={referrals.isLoading}
          error={referrals.isError}
          onRetry={() => void referrals.refetch()}
        />
        <KPICard
          label="Referral revenue"
          value={fmt.npr(referralTotals?.referred_revenue ?? 0)}
          loading={referrals.isLoading}
          error={referrals.isError}
          onRetry={() => void referrals.refetch()}
        />
        <KPICard
          label="Referral CAC"
          value={fmt.npr(referralTotals?.incentive_cac ?? null)}
          goodWhenUp={false}
          loading={referrals.isLoading}
          error={referrals.isError}
          onRetry={() => void referrals.refetch()}
        />
      </div>

      <div className="mt-5">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="coupons">Coupons</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
          </TabsList>
          <TabsContent value="coupons">
            <CouponsAnalyticsBody showKpis={false} detailReturnTo="/growth/coupons-referrals?tab=coupons" />
          </TabsContent>
          <TabsContent value="referrals">
            <ReferralsAnalyticsBody showKpis={false} detailReturnTo="/growth/coupons-referrals?tab=referrals" />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
